import { useCallback, useEffect, useState } from 'react';

import { fetchIncoming, fetchVideos } from './api.js';
import { formatBytes, formatDuration, formatResolution } from './format.js';

function CopyLink({ id }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/${id}`;

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard access needs a secure context; the link is selectable regardless.
    }
  }, [url]);

  return (
    <span className="copy">
      <a className="copy__url" href={`/${id}`}>
        /{id}
      </a>
      <button type="button" className="copy__btn" onClick={copy} aria-label="Copy share link">
        {copied ? 'copied' : 'copy'}
      </button>
    </span>
  );
}

function Published({ videos }) {
  if (videos.length === 0) {
    return <p className="msg">Nothing published yet.</p>;
  }

  return (
    <ul className="rows">
      {videos.map((video) => (
        <li key={video.id} className="row">
          <a className="row__art" href={`/${video.id}`}>
            {video.poster ? (
              <img src={video.poster} alt="" loading="lazy" decoding="async" />
            ) : (
              <span className="card__art-empty" aria-hidden="true" />
            )}
          </a>

          <div className="row__main">
            <a className="row__title" href={`/${video.id}`}>
              {video.title}
            </a>
            <CopyLink id={video.id} />
          </div>

          <dl className="row__facts">
            <div>
              <dt>Source</dt>
              <dd className="mono">media/{video.dir}</dd>
            </div>
            <div>
              <dt>Quality</dt>
              <dd>
                {formatResolution(video.maxHeight) ?? '—'} · {video.variants} rungs ·{' '}
                {video.codecs.join('+') || '—'}
              </dd>
            </div>
            <div>
              <dt>Length</dt>
              <dd>{formatDuration(video.duration) ?? '—'}</dd>
            </div>
            <div>
              <dt>Size</dt>
              <dd>{formatBytes(video.sizeBytes) ?? '—'}</dd>
            </div>
          </dl>
        </li>
      ))}
    </ul>
  );
}

function Incoming({ pending }) {
  if (pending.length === 0) {
    return (
      <p className="msg">
        <span className="msg__hint">
          Nothing staged. rsync a tree into <code>incoming/</code>, then run{' '}
          <code>npm run publish</code>.
        </span>
      </p>
    );
  }

  return (
    <ul className="rows">
      {pending.map((item) => (
        <li key={item.dir} className={`row row--staged${item.ready ? ' row--ready' : ''}`}>
          <div className="row__main">
            <span className="row__title mono">incoming/{item.dir}</span>
            <span className={`state ${item.ready ? 'state--ready' : 'state--blocked'}`}>
              {item.ready ? 'ready to publish' : 'blocked'}
            </span>
          </div>

          {item.ready ? (
            <dl className="row__facts">
              <div>
                <dt>Quality</dt>
                <dd>
                  {formatResolution(item.heights[0]) ?? '—'} · {item.variants} rungs ·{' '}
                  {item.codecs.join('+') || '—'}
                </dd>
              </div>
              <div>
                <dt>Files</dt>
                <dd>{item.segments}</dd>
              </div>
            </dl>
          ) : (
            <ul className="problems">
              {item.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function Browse() {
  const [state, setState] = useState({ status: 'loading', videos: [], pending: [], error: null });

  const load = useCallback(async () => {
    try {
      const [videos, pending] = await Promise.all([fetchVideos(), fetchIncoming()]);
      setState({ status: 'ready', videos, pending, error: null });
    } catch (error) {
      setState((prev) => ({ ...prev, status: 'error', error }));
    }
  }, []);

  useEffect(() => {
    load();
    // Staged trees change while an rsync runs, so this view refreshes itself.
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);

  if (state.status === 'loading') return <p className="msg">Loading…</p>;
  if (state.status === 'error') {
    return <p className="msg msg--bad">Could not load: {state.error.message}</p>;
  }

  return (
    <div className="browse">
      <section>
        <h2 className="browse__head">
          Published <span className="count">{state.videos.length}</span>
        </h2>
        <Published videos={state.videos} />
      </section>

      <section>
        <h2 className="browse__head">
          Staged <span className="count">{state.pending.length}</span>
        </h2>
        <Incoming pending={state.pending} />
      </section>
    </div>
  );
}
